import { useState } from 'react';
import { api } from '../lib/api.js';
import { Button, Input, Field, Alert, Badge, SectionTitle } from './ui.jsx';

/**
 * Connect the brand's own Stripe or Shopify.
 *
 * The highest-quality tracking available, and the only kind that needs no code
 * whatsoever: both platforms already know when a sale happened, because they
 * processed it. A merchant creates one webhook and pastes the secret back.
 */
const PLATFORMS = {
  stripe: {
    label: 'Stripe',
    why: 'The sales your creators bring, and every renewal of them, straight from Stripe. The only way recurring commissions work without you writing anything.',
    where: 'Stripe Dashboard → Developers → Webhooks → Add endpoint',
    secretLabel: 'Signing secret',
    secretHint: 'Starts with whsec_ — shown after you create the endpoint.',
    extra:
      'Pass the visitor’s aff_click into the Checkout Session as client_reference_id or metadata.aff_click — one line, and it is the only code you write. Renewals need nothing.',
  },
  shopify: {
    label: 'Shopify',
    why: 'Reads the real order, so ad blockers cannot cost you conversions and the value is always right.',
    where: 'Shopify Admin → Settings → Notifications → Webhooks → Create webhook',
    secretLabel: 'Webhook signing secret',
    secretHint: 'Shown at the bottom of the Notifications page, under your webhook list.',
    extra:
      'Nothing else to do. Shopify records the URL the customer landed on, which already contains aff_click — so attribution works with no code at all.',
  },
};

/** `only` limits the cards to those platforms (default: all of them). */
export function PlatformConnect({ programId, platforms, onChanged, only, title = true }) {
  const shown = Object.entries(PLATFORMS).filter(([key]) => !only || only.includes(key));
  if (!platforms || shown.length === 0) return null;

  return (
    <section>
      {title && (
        <SectionTitle hint="No code, and can't be blocked — the best option if you use either">
          Connect your platform
        </SectionTitle>
      )}
      <div className="space-y-3">
        {shown.map(([key, meta]) => (
          <PlatformCard
            key={key}
            programId={programId}
            platformKey={key}
            meta={meta}
            state={platforms[key]}
            onChanged={onChanged}
          />
        ))}
      </div>
    </section>
  );
}

function PlatformCard({ programId, platformKey, meta, state, onChanged }) {
  const [open, setOpen] = useState(false);
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  async function connect(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.post(`/programs/${programId}/connect/${platformKey}`, { secret });
      setSecret('');
      setOpen(false);
      onChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function disconnect() {
    setBusy(true);
    try {
      await api.delete(`/programs/${programId}/connect/${platformKey}`);
      onChanged?.();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className={`rounded-lg border p-4 ${
        state?.connected ? 'border-positive/25 bg-positive-soft' : 'border-rule bg-paper-raised'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-xl">
          <div className="flex items-center gap-2">
            <h3 className="font-medium">{meta.label}</h3>
            {state?.connected ? (
              <Badge tone="positive">Connected</Badge>
            ) : (
              <Badge tone="accent">Recommended</Badge>
            )}
          </div>
          <p className="mt-1 text-sm text-ink-muted">{meta.why}</p>
        </div>

        {state?.connected ? (
          <Button size="sm" variant="ghost" onClick={disconnect} disabled={busy}>
            Disconnect
          </Button>
        ) : (
          <Button size="sm" onClick={() => setOpen((v) => !v)}>
            {open ? 'Cancel' : `Connect ${meta.label}`}
          </Button>
        )}
      </div>

      {open && !state?.connected && (
        <form onSubmit={connect} className="mt-4 space-y-3 border-t border-rule pt-4">
          <ol className="space-y-3 text-sm">
            <li>
              <span className="font-medium">1.</span> Go to <em>{meta.where}</em>
            </li>
            <li>
              <span className="font-medium">2.</span> Point it at this URL:
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <code className="overflow-x-auto rounded bg-paper-sunken px-2 py-1 text-xs">
                  {state?.webhookUrl}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  type="button"
                  onClick={async () => {
                    await navigator.clipboard.writeText(state.webhookUrl);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                >
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </li>
            <li>
              <span className="font-medium">3.</span> Subscribe to:{' '}
              {state?.events?.map((e) => (
                <code key={e} className="mr-1 rounded bg-paper-sunken px-1.5 py-0.5 text-xs">
                  {e}
                </code>
              ))}
            </li>
          </ol>

          <Field
            label={meta.secretLabel}
            required
            error={error && secret.length >= 8 ? error : undefined}
            constraint="At least 8 characters — copied exactly from your dashboard"
            hint={meta.secretHint}
          >
            <Input
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={platformKey === 'stripe' ? 'whsec_…' : 'Paste it here'}
              required
            />
          </Field>

          <Alert>{error}</Alert>

          <Button type="submit" disabled={busy || secret.length < 8}>
            {busy ? 'Connecting…' : 'Connect'}
          </Button>

          <p className="text-xs text-ink-faint">{meta.extra}</p>
        </form>
      )}

      {state?.connected && <p className="mt-2 text-xs text-ink-muted">{meta.extra}</p>}
    </div>
  );
}
